import React, {Suspense, useEffect, useState} from 'react';
import LoadingScreen from '@/components/LoadingScreen/LoadingScreen';
import Sidebar from '@/components/Sidebar/Sidebar';
import Header from '@/components/Header/Header';
import {
    ActionIcon,
    AppShell,
    Box,
    Burger,
    Button,
    Center,
    Drawer,
    Group,
    Indicator,
    LoadingOverlay,
    ScrollArea,
    Stack,
    Text,
    Tooltip,
    useMantineColorScheme,
} from '@mantine/core';
import {useDisclosure, useHotkeys} from '@mantine/hooks';
import logoBeWhite from '@/assets/bobex.be_white.png';
import logoNlWhite from '@/assets/bobex.nl_white.png';
import logoCiWhite from '@/assets/ci.white.png';
import {ColorSchemeToggle} from '@bobex/react-core/components/ColorSchemeToggle/ColorSchemeToggle';
import {LanguagePicker} from '@bobex/react-core/components/LanguagePicker/LanguagePicker';
import {RouteUtils} from '@bobex/react-core/routes/RouteUtils';
import {useBoLoginInit} from '@bobex/react-core/utils/hooks/useBoLoginInit';
import {
    IconBell,
    IconBrandWhatsapp,
    IconCalendar,
    IconCurrencyEuro,
    IconDialpad,
    IconSearch,
    IconSettings,
    IconX,
} from '@tabler/icons-react';
import UserMenu from '@/components/UserMenu/UserMenu';
import NotificationMenu from '@/components/Layout/NotificationMenu';
import GlobalCrmButton from '@/components/Layout/GlobalCrmButton';
import RightDrawer from '@/components/SearchHeader/RightDrawer';
import ToolsDrawer from '@/components/Layout/ToolsDrawer';
import {useTranslation} from 'react-i18next';
import {AccountService} from '@bobex/react-core/services/AccountService';
import {Link, Outlet, useLocation} from 'react-router-dom';
import WhatsAppDrawer from '../WhatsAppDrawer/WhatsAppDrawer';

import {useWhatsAppNotifications} from '@bobex/react-core/domain/WhatsAppModel/hooks';
import {useReminderNotifications} from '@bobex/react-core/domain/TodoModel/hooks/useReminderNotifications';
import {useNotificationCounts} from '@bobex/react-core/domain/NotificationModel/hooks/useNotificationCounts';
import {AuthoritiesConstants} from '@bobex/react-core/domain/AuthoritiesModel/constants/AuthoritiesConstants';
import {modals} from '@mantine/modals';
import {godModeStore} from '@/components/EasterEggs/godmode/godModeStore';
import {GodModeDashboard} from '@/components/EasterEggs/godmode/GodModeDashboard';
import {AchievementTracker} from '@/components/EasterEggs/achievements/AchievementTracker';

export default function AppLayout() {
    const {t} = useTranslation();
    const user = AccountService.getUser();
    const canViewNotificationMenu = AccountService.hasAuthority([AuthoritiesConstants.VIEW_NOTIFICATIONS]);
    const onCallCalendar = useLocation().pathname.includes('call-calendar');
    const [mobileOpened, {toggle: toggleMobile}] = useDisclosure();
    const [desktopOpened, {toggle: toggleDesktop}] = useDisclosure(true);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [rightDrawerTab, setRightDrawerTab] = useState<'search' | 'incall'>('search');
    const [whatsappDrawer, setWhatsappDrawer] = useState(false);
    const [toolsDrawerOpen, {open: openToolsDrawer, close: closeToolsDrawer}] = useDisclosure(false);
    // Legacy -> React login handshake (one-shot ?token=...), see useBoLoginInit
    const {isLoading, failed: loginFailed, retry: retryLogin} = useBoLoginInit();
    const [godMode, setGodMode] = useState(godModeStore.isActive());

    // Show/hide the header ⚡ as God Mode activates/expires.
    useEffect(() => godModeStore.subscribe(() => setGodMode(godModeStore.isActive())), []);

    // Subscribe to WhatsApp notifications only when logged in
    const whatsappUnreadCount = useWhatsAppNotifications(!isLoading);

    // Subscribe to today's call-calendar reminders only when logged in
    const reminderCount = useReminderNotifications(!isLoading);

    const notificationCounts = useNotificationCounts(!isLoading && !!canViewNotificationMenu);

    const context = RouteUtils.getContext();
    const colorScheme = useMantineColorScheme();

    const date = new Date();

    //get i18n locale language
    const {i18n} = useTranslation();
    const locale = i18n.language.replace('_', '-');

    useHotkeys([
        [
            'mod+K',
            () => {
                setRightDrawerTab('search');
                setIsDrawerOpen((v) => !v);
            },
        ],
    ]);

    useHotkeys([
        [
            'mod+alt+W',
            () => {
                setWhatsappDrawer((v) => !v);
            },
        ],
    ]);

    const getLogo = () => {
        switch (context) {
            case 'be':
                return logoBeWhite;
            case 'nl':
                return logoNlWhite;
            case 'ci':
                return logoCiWhite;
            default:
                return logoBeWhite;
        }
    };

    if (loginFailed) {
        return (
                <Center h="100vh">
                    <Stack align="center" gap="sm">
                        <Text fw={600}>{t('login.boLoginFailedTitle', 'Could not sign you in automatically')}</Text>
                        <Text size="sm" c="dimmed">
                            {t('login.boLoginFailedText', 'The login handshake with the backoffice failed several times in a row.')}
                        </Text>
                        <Button onClick={retryLogin}>{t('login.boLoginRetry', 'Try again')}</Button>
                    </Stack>
                </Center>
        );
    }

    if (isLoading) {
        return (
                <LoadingOverlay
                        visible={true}
                        zIndex={1000}
                        overlayProps={{radius: 'sm', blur: 2}}
                        loaderProps={{type: 'bars'}}
                />
        );
    }

    return (
            <AppShell
                    layout={'alt'}
                    bg={'bodyBackgroundColor'}
                    header={{
                        height: 64,
                    }}
                    navbar={{
                        width: 250,
                        breakpoint: 'lg',
                        collapsed: {mobile: true, desktop: !desktopOpened},
                    }}
            >
                <AppShell.Header>
                    {/* Mobile: single-line header — Burger | Toggle + Date + Lang (center) | Tools */}
                    <Group
                            hiddenFrom="lg"
                            h="100%"
                            px="xs"
                            wrap="nowrap"
                            align="center"
                            bg="sidebarBackgroundColor"
                            style={{padding: '0.8rem', zIndex: 1000, position: 'relative'}}
                            grow
                            gap={'sm'}
                    >
                        <Box w={36}>
                            <Burger
                                    opened={mobileOpened}
                                    onClick={toggleMobile}
                                    size="sm"
                            />
                        </Box>
                        <Group wrap="nowrap" gap="xs" justify="center" style={{flex: 1}}>
                            <ColorSchemeToggle/>
                            <Box component="span"
                                 style={{fontSize: 'var(--mantine-font-size-sm)', whiteSpace: 'nowrap'}}>
                                {new Intl.DateTimeFormat(locale, {
                                    dateStyle: 'short',
                                    timeZone: 'UTC',
                                }).format(date)}
                            </Box>
                            {context === 'be' || context === 'ci' ? <LanguagePicker/> : null}
                        </Group>
                        <Box w={36} style={{display: 'flex', justifyContent: 'flex-end'}}>
                            <Tooltip label={t('Tools')}>
                                <ActionIcon
                                        variant="default"
                                        size="lg"
                                        aria-label={t('Tools')}
                                        onClick={openToolsDrawer}
                                >
                                    <IconSettings stroke={1.5}/>
                                </ActionIcon>
                            </Tooltip>
                        </Box>
                    </Group>

                    {/* Desktop: full header */}
                    <Group
                            visibleFrom="lg"
                            h="100%"
                            px="sm"
                            wrap="nowrap"
                            align="center"
                            gap="xs"
                            bg="sidebarBackgroundColor"
                            style={{zIndex: 1000, position: 'relative'}}
                    >
                        <Burger
                                opened={desktopOpened}
                                onClick={toggleDesktop}
                                size="sm"
                        />
                        <Header/>
                        <Box style={{flex: 1}}/>
                        {godMode ? (
                            <Tooltip label="God Mode">
                                <ActionIcon
                                        variant="default"
                                        size="lg"
                                        aria-label="God Mode"
                                        onClick={() => modals.open({
                                            title: '⚡ GOD MODE',
                                            centered: true,
                                            size: 'lg',
                                            children: <GodModeDashboard/>,
                                        })}
                                >
                                    <span style={{fontSize: 18, lineHeight: 1}}>⚡</span>
                                </ActionIcon>
                            </Tooltip>
                        ) : null}
                        <ColorSchemeToggle/>
                        <Box component="span" style={{whiteSpace: 'nowrap', fontSize: 'var(--mantine-font-size-sm)'}}>
                            {new Intl.DateTimeFormat(locale, {
                                dateStyle: 'short',
                                timeZone: 'UTC',
                            }).format(date)}
                        </Box>
                        {context === 'be' || context === 'ci' ? <LanguagePicker/> : null}
                        <Box style={{flex: 1}}/>
                        <Tooltip label={'ctrl + k'}>
                            <ActionIcon
                                    variant="default"
                                    size="lg"
                                    aria-label="Search"
                                    onClick={() => {
                                        setRightDrawerTab('search');
                                        setIsDrawerOpen((v) => !v);
                                    }}
                            >
                                <IconSearch stroke={1.5}/>
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={'ctrl + alt + W'}>
                            <Indicator
                                    inline
                                    label={whatsappUnreadCount > 0 ? whatsappUnreadCount : 0}
                                    size={16}
                                    color="red"
                                    disabled={whatsappUnreadCount == undefined || whatsappUnreadCount === 0}
                            >
                                <ActionIcon
                                        variant="default"
                                        size="lg"
                                        aria-label="WhatsApp"
                                        onClick={() => {
                                            setWhatsappDrawer(true);
                                        }}
                                >
                                    <IconBrandWhatsapp stroke={1.5}/>
                                </ActionIcon>
                            </Indicator>
                        </Tooltip>

                        {canViewNotificationMenu ? <NotificationMenu counts={notificationCounts}/> : null}

                        {canViewNotificationMenu && (
                            <Tooltip label={t('Unpaid clients')}>
                                <Indicator
                                    inline
                                    label={notificationCounts.unpaid}
                                    size={16}
                                    color="red"
                                    disabled={notificationCounts.unpaid === 0}
                                >
                                    <ActionIcon
                                        variant="default"
                                        size="lg"
                                        aria-label={t('Unpaid clients')}
                                        component={Link}
                                        to="/notifications?unpaid=true"
                                    >
                                        <IconCurrencyEuro stroke={1.5}/>
                                    </ActionIcon>
                                </Indicator>
                            </Tooltip>
                        )}
                        <Tooltip label={t('Reminders')}>
                            <Indicator
                                    inline
                                    label={reminderCount > 0 ? reminderCount : 0}
                                    size={16}
                                    color="red"
                                    disabled={reminderCount == undefined || reminderCount === 0}
                            >
                                <ActionIcon
                                        variant="default"
                                        size="lg"
                                        aria-label="Reminders"
                                        component="a"
                                        href={
                                            user?.userID
                                                    ? RouteUtils.getBasename() + '/call-calendar'
                                                    : '#'
                                        }
                                        target={onCallCalendar ? '_self' : '_blank'}
                                        rel="noopener noreferrer"
                                >
                                    <IconBell stroke={1.5}/>
                                </ActionIcon>
                            </Indicator>
                        </Tooltip>
                        <Tooltip label={t('Calendar')}>
                            <ActionIcon
                                    variant="default"
                                    size="lg"
                                    aria-label="Calendar"
                                    component="a"
                                    href={
                                        user?.userID
                                                ? RouteUtils.getBasename() + '/call-calendar'
                                                : '#'
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                            >
                                <IconCalendar stroke={1.5}/>
                            </ActionIcon>
                        </Tooltip>
                        <GlobalCrmButton/>
                        <UserMenu/>
                        <Tooltip label={t('Incall')}>
                            <ActionIcon
                                    variant="default"
                                    size="lg"
                                    aria-label="Incall"
                                    onClick={() => {
                                        setRightDrawerTab('incall');
                                        setIsDrawerOpen(true);
                                    }}
                            >
                                <IconDialpad stroke={1.5}/>
                            </ActionIcon>
                        </Tooltip>
                    </Group>

                    <ToolsDrawer
                            opened={toolsDrawerOpen}
                            onClose={closeToolsDrawer}
                            onOpenSearch={() => {
                                setRightDrawerTab('search');
                                setIsDrawerOpen(true);
                            }}
                            onOpenIncall={() => {
                                setRightDrawerTab('incall');
                                setIsDrawerOpen(true);
                            }}
                            onOpenWhatsApp={() => setWhatsappDrawer(true)}
                            whatsappUnreadCount={whatsappUnreadCount}
                            reminderCount={reminderCount}
                            user={user}
                    />
                    <RightDrawer
                            isVisible={isDrawerOpen}
                            onClose={() => setIsDrawerOpen(false)}
                            initialTab={rightDrawerTab}
                    />
                    <WhatsAppDrawer
                            isOpen={whatsappDrawer}
                            onClose={() => setWhatsappDrawer(false)}
                            unreadCount={whatsappUnreadCount}
                    />

                </AppShell.Header>
                {/* Desktop navbar */}
                <AppShell.Navbar
                        bg={'navbarBackgroundColor'}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            overflow: 'hidden',
                            ...(context === 'nl' ? {borderRight: '6px solid #ff6b35'} : {}),
                            ...(context === 'ci' ? {borderRight: '6px solid #39f'} : {}),
                        }}
                        visibleFrom="lg"
                >
                    <AppShell.Section style={{flexShrink: 0}}>
                        <Group ps="md" pe="xs" h={64} justify="space-between" wrap="nowrap">
                            <img src={getLogo()} alt="Bobex" width={150}/>
                        </Group>
                    </AppShell.Section>
                    <AppShell.Section
                            p="md"
                            pt={0}
                            style={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                    >
                        <Sidebar/>
                    </AppShell.Section>
                </AppShell.Navbar>

                {/* Mobile drawer */}
                <Drawer
                        opened={mobileOpened}
                        onClose={toggleMobile}
                        position="left"
                        size="xs"
                        padding={0}
                        withCloseButton={false}
                        styles={{
                            body: {
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'var(--mantine-color-navbarBackgroundColor)'
                            }
                        }}
                        hiddenFrom="lg"
                >
                    <Group ps="md" pe="xs" h={56} justify="space-between" wrap="nowrap" bg="navbarBackgroundColor">
                        <img src={getLogo()} alt="Bobex" width={120}/>
                        <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="lg"
                                aria-label={t('Close menu')}
                                onClick={toggleMobile}
                        >
                            <IconX stroke={1.5}/>
                        </ActionIcon>
                    </Group>
                    <ScrollArea style={{flex: 1}} p="md" pt={0} bg="navbarBackgroundColor">
                        <Sidebar/>
                    </ScrollArea>
                </Drawer>
                <AppShell.Main>
                    <Suspense fallback={<LoadingScreen/>}>
                        <Outlet/>
                    </Suspense>
                </AppShell.Main>
                <AchievementTracker/>
            </AppShell>
    );
}
